// components/BottomSheet.tsx
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  CloseButton,
  Drawer,
  DrawerContent,
  DrawerOverlay,
} from "@chakra-ui/react";
import {
  closeSheet,
  selectBottomSheet,
} from "../../store/slices/BottomSheetSlice";
import { throttle } from "lodash";

const BottomSheet = () => {
  const dispatch = useDispatch();

  const {
    isOpen,
    component: Component,
    props,
    height: initialHeight,
  } = useSelector(selectBottomSheet);
  const [sheetHeight, setSheetHeight] = useState(initialHeight || 300);
  const dragRef = useRef<HTMLDivElement | null>(null);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);

  // Reset height when sheet opens
  useEffect(() => {
    if (isOpen) {
      setSheetHeight(initialHeight || 300);
    }
  }, [isOpen, initialHeight]);

  const handleClose = () => {
    dispatch(closeSheet());
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    startYRef.current = clientY;
    startHeightRef.current = sheetHeight;

    if ("touches" in e) {
      document.addEventListener("touchmove", throttledDragMove);
      document.addEventListener("touchend", handleDragEnd);
    } else {
      document.addEventListener("mousemove", throttledDragMove);
      document.addEventListener("mouseup", handleDragEnd);
    }
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    const clientY =
      "touches" in e
        ? (e as TouchEvent).touches[0].clientY
        : (e as MouseEvent).clientY;
    const deltaY = startYRef.current - clientY;
    const newHeight = Math.max(
      0,
      Math.min(window.innerHeight - 100, startHeightRef.current + deltaY)
    );
    setSheetHeight(newHeight);
  };

  // Throttle to limit setSheetHeight updates
  const throttledDragMove = throttle(handleDragMove, 50);

  const handleDragEnd = () => {
    const MIN_HEIGHT_TO_CLOSE = 200;

    if (sheetHeight < MIN_HEIGHT_TO_CLOSE) {
      handleClose();
    }

    document.removeEventListener("mousemove", throttledDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", throttledDragMove);
    document.removeEventListener("touchend", handleDragEnd);
  };

  if (!Component) return null;

  return (
    <Drawer
      isOpen={isOpen}
      placement="bottom"
      onClose={handleClose}
      size="full"
    >
      <DrawerOverlay />
      <DrawerContent
        background={"transparent"}
        maxHeight={`${sheetHeight}px`}
        transition="height 0.2s ease"
        overflow="hidden"
        position="relative"
        px={"1rem"}
        pt={".3rem"}
        width={{ base: "none", md: "40rem" }}
        mx={"auto"}
      >
        <Box
          h={"100%"}
          bg={"background"}
          border={"1px solid transparent"}
          borderColor={"neutral.900"}
          borderTopRadius="20px"
          boxShadow={"0px 0px 4px 2px rgba(225,225,225,0.15)"}
        >
          <Box
            ref={dragRef}
            position="absolute"
            top="4"
            left="50%"
            transform="translateX(-50%)"
            width="50px"
            height="5px"
            my="3"
            borderRadius="full"
            bg="gray.300"
            cursor="grab"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            _active={{ cursor: "grabbing" }}
          />

          <CloseButton
            onClick={handleClose}
            position={"absolute"}
            size={"md"}
            top={"4"}
            right={8}
          />

          <Box pt="14" px="4">
            <Component {...props} onClose={handleClose} />
          </Box>
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default BottomSheet;
